/* eslint-disable @typescript-eslint/ban-ts-comment */
import { defineStore } from 'pinia'
import { computed, reactive, onUnmounted, watch } from 'vue'
import { store } from '@remote/store'
import adapterRequest from '@remote/api/adapter'
import { getDiskNameList } from '@remote/api/adapter/gx-mqtt'
import { getNetDrives as getNetDrivesApi, removeNetDrives as removeNetDrivesApi } from '@remote/api/adapter/disk-mqtt'
import { requestAnimationFrame } from '@remote/utils/base'
import { IDiskPartition, IDiskPartitionItem, INetDrives, INetDriveUrl } from '@remote/type/hezi'
import { IErrorRes } from '@remote/type'
import { toast } from '@remote/utils/dialog'

let loopInterval = 60 * 1000
const diskExceptionLoopInterval = 10 * 1000

type TPath2AliasMap = { [path: string]: string }
let path2AliasMap: TPath2AliasMap

export function getNetDeiveInfoByUrl (url: string): INetDriveUrl {
  const protocol = url.includes('://') ? url.split(':')[0] : ''
  const urlSplitRes = url.split('/')
  const ip = urlSplitRes[2]
  const path = urlSplitRes[urlSplitRes.length - 1]
  const type = protocol === 'smb' ? 'Samba' : 'WebDAV'
  return { protocol, ip, path, type }
}

export function getPercentAndBgColor (free: number, size: number) {
  const percent = Math.floor(100 * (size - free) / size)
  if (percent <= 70) {
    return { color: 'blue', percent }
  }
  if (percent <= 90) return { color: 'orange', percent }
  return { color: 'red', percent }
}

export const mergeDiskAlias = (names: TPath2AliasMap) => {
  path2AliasMap = Object.assign({}, path2AliasMap, names)
  console.log('mergeDiskAlias', path2AliasMap)
}

export const formatList = ['ntfs', 'vfat', 'exfat', 'ext2', 'ext3', 'ext4']

export const globalDisk: Partial<{ 
  builtInDisk: IDiskPartitionItem | null
  disks: Array<IDiskPartitionItem>
  root: string
  diskException: boolean 
  netDrives:Array<INetDrives>
}> = {}

export const getBuildInDisk = (): (IDiskPartitionItem | null | undefined) => {
  return globalDisk.builtInDisk
}

export const getDefaultPath = (): string => {
  const mount = globalDisk.builtInDisk?.mount
  return mount ? `${mount}/我的下载` : ''
}

export const isBuildInPath = (path = ''):boolean => {
  const mount = globalDisk.builtInDisk?.mount
  return mount && path ? path.startsWith(mount) : false
}

export type TGetDisksResult = Promise<{ 
  root: string
  builtInDisk: IDiskPartitionItem | null
  disks: Array<IDiskPartitionItem>
  externalPartitions: Array<TExternalPartitions>
  requestId?:number
}>

export type TExternalPartitions = IDiskPartition & {
  displayPartitions: IDiskPartitionItem[]
  storageInfo: {
    total: number
    used: number
    free: number
  }
}

export function getDisksApi (): Promise<[IErrorRes, { list: IDiskPartition[] }]> {
  return adapterRequest(
    '/vod/disks',
    {},
    { apiEnv: 'heziDevelopment' },
  )
}

export async function getDisks (requestId?:number): TGetDisksResult {
  // 获取磁盘别名
  if (!path2AliasMap) {
    const diskNameListRes = await getDiskNameList().catch(err => {
      console.log('getDiskNameList error', err)
    })

    if (diskNameListRes && diskNameListRes?.ret === 0 && diskNameListRes?.result?.disk_infos?.length) {
      path2AliasMap = {}
      diskNameListRes?.result?.disk_infos.forEach(item => {
        path2AliasMap[item.path] = item.disk_name
      })
    }
  }

  const [err, res]: [IErrorRes, { list: IDiskPartition[] }] = await getDisksApi()
  let builtInDisk: IDiskPartitionItem | null = null
  let disks: IDiskPartitionItem[] = []
  let root = ''
  const externalPartitions: TExternalPartitions[] = []
  if (!err && res?.list?.length) {
    disks = res.list.reduce((result: IDiskPartitionItem[], disk) => {
      if (!disk?.partitions?.length) {
        // 磁盘未分区单独处理
        // return result
        // @ts-ignore
        disk.partitions = [{ ...disk }]
      }
      const storageInfo = {
        total: 0,
        used: 0,
        free: 0,
        status: '',
        statusText: '',
      }
      const partitions = disk.partitions?.map((item) => {
        item.removable = disk.removable
        const displayName = (path2AliasMap && path2AliasMap[item.mount] || `${disk.mode}`)
        item.display_name = displayName.length > 15 ? displayName.slice(0, 5) + '...' + displayName.slice(-5) : displayName
        item.full_display_name = displayName
        item.pname = disk.name

        if (item.mount) {
          const { percent, color } = getPercentAndBgColor(item.free, item.size)
          item.percent = percent
          item.bgColor = color
          storageInfo.total = storageInfo.total + item.size
          storageInfo.used = storageInfo.used + (item.size - item.free)
          storageInfo.free = storageInfo.free + item.free
        }
        if (disk.hanging || item.hanging) {
          item.status = 'hanging'
          item.statusText = !disk.removable ? '磁盘读写异常，请重启设备' : '磁盘读写异常'
        } else if (item.mount) {
          item.status = 'mounted'
        } else if (item.formatting) {
          item.status = 'formatting'
          item.statusText = '磁盘格式化中...'
        } else if (item.mounting) {
          item.status = 'mounting'
          item.statusText = '自动挂载中，请耐心等待'
        } else if (item.type && item.type !== 'unknown' && !formatList.includes(item.type)) {
          item.status = 'typeNotSupport'
          item.statusText = '磁盘格式暂不支持激活'
        } else if (item.type) {
          item.status = 'inactive'
          item.statusText = !disk.removable ? '磁盘未就绪' : '磁盘未就绪，激活将挂载磁盘'
        } else {
          item.status = 'unformatted'
          item.statusText = '磁盘未就绪，格式化将清空磁盘'
        }

        if (!disk.removable) {
          item.display_name = '内置磁盘'
          item.full_display_name = '内置磁盘'
          builtInDisk = item
          root = item.mount
        }
        return item
      })

      if (disk?.hanging) {
        storageInfo.status = 'hanging'
        storageInfo.statusText = '磁盘读写异常，请重新拔插磁盘'
      } else if (partitions.some(item => item.mounting)) {
        storageInfo.status = 'mounting'
        storageInfo.statusText = '自动挂载中，请耐心等待'
      } else if (partitions.some(item => item.mount)) {
        storageInfo.status = 'mounted'
      } else if (partitions.some(item => item.formatting)) {
        storageInfo.status = 'formatting'
        storageInfo.statusText = '磁盘格式化中，请耐心等待'
      } else if (partitions.some(item => {
        return item.type && item.type !== 'unknown' && !formatList.includes(item.type)
      })) {
        storageInfo.status = 'typeNotSupport'
        storageInfo.statusText = '磁盘格式暂不支持激活'
      } else {
        storageInfo.statusText = '磁盘未就绪'
      }

      if (disk.removable) externalPartitions.push(Object.assign({}, disk, { displayPartitions: partitions, storageInfo }))
      result = result.concat(partitions)
      return result
    }, [])
  }
  console.log('externalPartitions', externalPartitions)
  if (!root) root = disks[0]?.mount
  return { builtInDisk: builtInDisk, disks, root, externalPartitions, requestId }
}

export async function getNetDrives () {
  const netDrivesRes = await getNetDrivesApi().catch()
  let netDrives: INetDrives[] = []
  if (netDrivesRes?.ret === 0 && netDrivesRes?.result?.list?.length) {
    console.log('netDrivesRes', netDrivesRes.result?.list)
    netDrives = netDrivesRes.result?.list.map(item => ({ ...item, ...getNetDeiveInfoByUrl(item.url) }))
  }
  return netDrives
}

export const initData = {
  disks: [] as IDiskPartitionItem[],
  builtInDisk: null as IDiskPartitionItem | null,
  diskName: '内置磁盘',
  diskException: false,
  diskChange: false,
  externalPartitions: [] as TExternalPartitions[],
  netDrives: [] as INetDrives[],
}

export const useDisk = defineStore('heziDisk', () => {
  const data = reactive(initData)
  let timer: NodeJS.Timeout
  const getDeviceDisk = async () => {
    const { builtInDisk, disks, root, externalPartitions } = await getDisks()
    data.diskChange = !!(
      data.disks.length && data.disks.length !== disks?.length ||
      data.builtInDisk && !builtInDisk ||
      data.builtInDisk && builtInDisk && data.builtInDisk.mount !== builtInDisk.mount
    )
    data.builtInDisk = builtInDisk
    data.disks = disks
    data.diskException = !disks?.length
    data.externalPartitions = externalPartitions

    globalDisk.builtInDisk = builtInDisk
    globalDisk.disks = disks
    globalDisk.root = root
    globalDisk.diskException = data.diskException

    if (data.diskChange) {
      console.log('%c设备磁盘变更', 'color: red')
    }
    if (data.diskException) {
      console.error('设备磁盘异常')
    } else {
      console.log('%c设备磁盘正常', 'color: blue')
    }
    const netDrives = await getNetDrives()
    data.netDrives = netDrives
    globalDisk.netDrives = netDrives
    return { diskException: !disks?.length, builtInDisk, disks, root, netDrives }
  }

  const removeNetDrives = async (id: string) => {
    const index = data.netDrives.findIndex(item => item.id === id)
    if (index < 0) return
    const res = await removeNetDrivesApi(id)
    if (res?.ret === 0) {
      toast({type: 'success', message: '移除成功'})
      data.netDrives.splice(index, 1)
      globalDisk.netDrives?.splice(index, 1)
    } else {
      toast({type: 'error', message: '移除失败'})
      return res.msg || res.result.msg || '移除失败'
    }
  }
  const loop = async () => {
    if (timer) clearTimeout(timer)
    try {
      await getDeviceDisk()
    } catch (err) {
      console.log('getDeviceDisk', err)
    }
    timer = setTimeout(() => {
      requestAnimationFrame(loop)
    }, data.diskException ? diskExceptionLoopInterval : loopInterval)
  }

  const setLoopInterval = (interval: number, immediately = false) => {
    loopInterval = interval
    if (immediately) loop()
  }

  const target = computed(() => {
    return store.state.curDeviceRunner?.target
  })

  watch(target, async (val) => {
    if (val) {
      // getDeviceDisk()
      setTimeout(loop, loopInterval)
    }
  })

  onUnmounted(() => {
    if (timer) clearTimeout(timer)
  })

  return {
    loop,
    data,
    checkDeviceDisk: getDeviceDisk,
    setLoopInterval,
    removeNetDrives,
  }
})

